import { HttpHeaders } from '@angular/common/http';
import { Component } from "@angular/core";
import { AppService } from "../app.service";
import { FormControl, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { CryptoService } from "../crypto.service";
import { Device } from "../device";
import { MatDialog } from '@angular/material/dialog';
import { CreateWorkspaceKeyDialogComponent } from '../create-workspace-key-dialog/create-workspace-key-dialog.component';

@Component({
  selector: "app-workspace",
  standalone: false,
  templateUrl: "./workspace.component.html",
  styleUrls: ["./workspace.component.css"],
})
export class WorkspaceComponent {
  apiKey: any;
  status: any;
  workspaceName: any;
  displayedColumns: string[] = [
    "workspaceName",
    "apiKey",
    "status",
    "createdAt",
  ];
  workspaces: any = [];
  selected = new FormControl("valid", [
    Validators.required,
    Validators.pattern("valid"),
  ]);
  expandedElement: any = null;
  uploadKeyControl = new FormControl("", [Validators.required]);
  copiedStates: { [key: string]: boolean } = {};
  crDate: any;
  loading = true;
  dev: Device | any;

  constructor(
    public appService: AppService,
    public router: Router,
    private cs: CryptoService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.loading = true;
    this.listWorkspace();
  }

  configKey(id: string, name: string, key: string) {
    this.router.navigate(["/view-workspace/" + id + "/" + name + "/" + key]);
  }

  formatTimeStamp(ts: number): string {
    const timestamp = ts * 1000;
    const date = new Date(timestamp);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  toggleRow(row: any) {
    this.expandedElement = this.expandedElement === row ? null : row;
    this.uploadKeyControl.setValue("");
  }

  maskApiKey(apiKey: string): string {
    if (!apiKey || apiKey.length <= 10) {
      return apiKey;
    }
    const start = apiKey.substring(0, 3);
    const end = apiKey.substring(apiKey.length - 4);
    return `${start}${"*".repeat(6)}${end}`;
  }

  getCopyTooltip(apiKey: string): string {
    return this.copiedStates[apiKey] ? "Copied" : "Copy";
  }

  async copyMessage(message: string) {
    try {
      await navigator.clipboard.writeText(message);
      this.copiedStates[message] = true;
      setTimeout(() => {
        this.copiedStates[message] = false;
      }, 2000);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  }

  async listWorkspace() {
    let obj: any = {
      email: this.appService.get("xdbc.emailId"),
      routeType: "UI_SESSION"
    };

    try {
      const res = await this.appService.postRequest(obj, "auth/list-workspace");

      this.workspaces = res.data.map((workspace: any) => ({
        ...workspace,
        createdAt: workspace.createdAt * 1000,
      }));

      this.loading = false;

      const newList = this.workspaces;
      this.appService.updateWorkspaces(newList);

      let dwork = this.workspaces.filter((de: any) => de.default);
      this.appService.set("xdbc.workspace", JSON.stringify(dwork[0]));

      const wsJson = this.appService.get("xdbc.workspace");

      if (wsJson !== undefined && typeof wsJson === 'string' && wsJson.trim() !== '' && wsJson !== 'undefined') {
        try {
          const ws = JSON.parse(wsJson);
          this.appService.setDefaultVal = ws.default;
          this.appService.workspaceName = ws.workspaceName;

          // ✅ CHECK IF CRYPTO KEY EXISTS
          await this.checkWorkspaceCryptoKey(ws);

        } catch (err) {
          console.error('Invalid JSON:', err);
        }
      }

    } catch (error) {
      console.error("Error fetching workspaces", error);
      this.loading = false;
    }
  }

  // ✅ Check if workspace has crypto key
  async checkWorkspaceCryptoKey(workspace: any) {
    try {
      const workspaceName = workspace.workspaceName;
      const apiKey = workspace.apiKey;

      const storedDevice = await this.cs.GetWorkspace(workspaceName);

      if (!storedDevice || !storedDevice.KeyPair) {
        console.log(`No crypto key found for workspace: ${workspaceName}`);
        await this.showCreateKeyDialog(workspaceName, apiKey);
      } else {
        console.log(`Crypto key exists for workspace: ${workspaceName}`);
      }
    } catch (error) {
      console.error("Error checking workspace crypto key:", error);
    }
  }

  // ✅ Show Material Dialog
  async showCreateKeyDialog(workspaceName: string, apiKey: string) {
    const dialogRef = this.dialog.open(CreateWorkspaceKeyDialogComponent, {
      width: '540px',
      maxWidth: '90vw',
      data: { workspaceName: workspaceName },
      disableClose: true, // Prevent closing by clicking outside
      panelClass: 'custom-dialog-container'
    });

    const result = await dialogRef.afterClosed().toPromise();

    if (result === true) {
      await this.createAndStoreWorkspaceKey(workspaceName, apiKey);
    } else {
      this.appService.addErrorMessage(
        "Workspace key is required for secure operations."
      );
    }
  }

  // ✅ Create and store workspace key
  async createAndStoreWorkspaceKey(workspaceName: string, apiKey: string) {
    try {
      this.loading = true;

      // 1. Generate key pair
      const keyPair = await this.appService.getKeyPair();
      const publicKeyPEM = keyPair[0];
      const privateKeyPEM = keyPair[1];

      // 2. Submit public key
      const submitKeyPayload = {
        apiKey: apiKey,
        publicKey: this.appService.convertPEMToBase64(publicKeyPEM),
        routeType: "UI_SESSION"
      };

      let submitKeyResp = await this.appService.postRequest(
        submitKeyPayload,
        "auth/submit-key"
      );

      if (submitKeyResp?.status !== "Success") {
        this.appService.addErrorMessage(
          submitKeyResp?.message || "Key submission failed"
        );
        this.loading = false;
        return;
      }

      console.log("Key submitted successfully:", submitKeyResp);

      // 3. Store in crypto DB
      await this.storePrivateKeyInCryptoDB(privateKeyPEM, workspaceName, submitKeyResp?.data?.keyId);

      // 4. Activate workspace
      // await this.activateWorkspace(workspaceName, apiKey);

      // // 5. Download private key
      // setTimeout(() => {
      //   this.downloadFile(privateKeyPEM, workspaceName);
      //   this.appService.addSuccessMessage(
      //     "Private key downloaded successfully!"
      //   );
      // }, 1000);

      this.appService.addSuccessMessage(
        "Workspace key created successfully!"
      );

      this.loading = false;

    } catch (error: any) {
      console.error("Error creating workspace key:", error);
      this.appService.addErrorMessage(
        "Failed to create workspace key: " + (error.message || "Unknown error")
      );
      this.loading = false;
    }
  }

  async storePrivateKeyInCryptoDB(
    privateKeyPEM: string,
    workspaceName: string,
    keyId: number
  ): Promise<void> {
    try {
      this.dev = new Device();
      this.dev.WorkSpace = workspaceName;
      this.dev.KeyPair = await this.cs.buildKeyPairFromPEMAndBase64(privateKeyPEM);
      this.dev.KeyId = keyId;
      await this.cs.PersistWorkspace(this.dev);

      const storedDevice = await this.cs.GetWorkspace(workspaceName);
      if (!storedDevice || !storedDevice.KeyPair) {
        throw new Error("Failed to store private key");
      }
    } catch (error: any) {
      throw new Error("Failed to store private key: " + (error.message || "Unknown error"));
    }
  }

  async activateWorkspace(workspaceName: string, apiKey: string) {
    try {
      // const activationPayload = {
      //   apiKey: apiKey,
      //   routeType: "UI_SESSION"
      // };

      const activationPayload = {
        name: workspaceName,
        routeType: "UI_SESSION"
      };

      const signatureData = await this.appService.wssign(
        activationPayload,
        workspaceName
      );

      if (!signatureData) {
        throw new Error("Failed to sign activation payload");
      }

      const signature = new TextDecoder().decode(signatureData);

      const response = await this.appService.wsPostRequestWithHeaders(
        activationPayload,
        "auth/activate",
        apiKey,
        signature
      );

      if (response?.status !== "Success") {
        throw new Error(response?.message || "Activation failed");
      }
    } catch (error: any) {
      throw new Error("Failed to activate workspace: " + (error.message || "Unknown error"));
    }
  }

  downloadFile(pkeyStr: string, wpsName: string) {
    const a = window.document.createElement("a");
    a.href = window.URL.createObjectURL(
      new Blob([pkeyStr], { type: "text/plain" })
    );
    a.download = wpsName + "_privatekey.pem";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}